import React from "react";
import { Building2 } from "lucide-react";

// Flatten org chart tree for iterative rendering
function flattenOrgTree(nodes, parentId = null, level = 0) {
  const result = [];
  if (!nodes) return result;
  
  const items = Array.isArray(nodes) ? nodes : [nodes];
  for (const node of items) {
    result.push({ ...node, parentId, level });
    if (node.children && node.children.length > 0) {
      result.push(...flattenOrgTree(node.children, node.id, level + 1));
    }
  }
  return result;
}

// Org Chart - iterative rendering
export function OrgChartDisplay({ tree }) {
  if (!tree || (Array.isArray(tree) && tree.length === 0)) {
    return null;
  }

  const rootNodes = Array.isArray(tree) ? tree : [tree];
  
  const renderNode = (node, isRoot) => (
    <div key={node.id} className="flex flex-col items-center">
      <div className={`border-2 rounded-sm p-3 min-w-[180px] text-center bg-white ${isRoot ? 'border-blue-900 bg-blue-50' : 'border-slate-300'}`}>
        <p className="font-semibold text-slate-900 text-sm">{node.person_name}</p>
        <p className="text-xs text-slate-500 mt-1">{node.role_title}</p>
      </div>
    </div>
  );

  const renderLevel = (nodes, isRoot = false) => {
    if (!nodes || nodes.length === 0) return null;
    
    return (
      <div className="flex flex-col items-center">
        <div className="flex gap-8">
          {nodes.map(node => (
            <div key={node.id} className="flex flex-col items-center">
              {renderNode(node, isRoot)}
              {node.children && node.children.length > 0 && (
                <React.Fragment>
                  <div className="w-px h-6 bg-slate-300" />
                  {node.children.length > 1 && (
                    <div className="flex justify-center w-full">
                      <div className="h-px bg-slate-300" style={{ width: `${Math.max(1, node.children.length - 1) * 200}px` }} />
                    </div>
                  )}
                  <div className="flex gap-8">
                    {node.children.map(child => (
                      <div key={child.id} className="flex flex-col items-center">
                        <div className="w-px h-6 bg-slate-300" />
                        {renderNode(child, false)}
                        {child.children && child.children.length > 0 && (
                          <React.Fragment>
                            <div className="w-px h-6 bg-slate-300" />
                            <div className="flex gap-8">
                              {child.children.map(grandchild => (
                                <div key={grandchild.id} className="flex flex-col items-center">
                                  <div className="w-px h-6 bg-slate-300" />
                                  {renderNode(grandchild, false)}
                                </div>
                              ))}
                            </div>
                          </React.Fragment>
                        )}
                      </div>
                    ))}
                  </div>
                </React.Fragment>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  return renderLevel(rootNodes, true);
}

// Alias for backward compatibility
export const OrgChartNode = OrgChartDisplay;

// Group Structure Tree - iterative
export function GroupTreeNode({ node, currentCompanyId, onNavigate, level = 0 }) {
  if (!node) return null;
  const isCurrentCompany = node.id === currentCompanyId;
  
  const NodeBox = ({ n, lvl }) => {
    const isCurrent = n.id === currentCompanyId;
    return (
      <div className={lvl > 0 ? "ml-8 mt-2" : ""}>
        <div className="flex items-center gap-2">
          {lvl > 0 && <div className="w-6 h-px bg-slate-300" />}
          <div 
            className={`flex items-center gap-2 p-2 rounded-sm cursor-pointer transition-colors border ${isCurrent ? 'bg-blue-50 border-blue-300' : 'bg-white border-slate-200 hover:bg-slate-50'}`}
            onClick={() => !isCurrent && onNavigate(n.id)}
          >
            <Building2 className={`w-4 h-4 ${isCurrent ? 'text-blue-900' : 'text-slate-400'}`} />
            <span className={`text-sm ${isCurrent ? 'font-medium text-blue-900' : 'text-slate-600'}`}>{n.name}</span>
            {n.ownership_percentage && <span className="text-xs text-slate-400 ml-2">({n.ownership_percentage.toFixed(1)}%)</span>}
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      <NodeBox n={node} lvl={level} />
      {node.children && node.children.length > 0 && (
        <div className="border-l border-slate-200 ml-4">
          {node.children.map(child => (
            <div key={child.id}>
              <NodeBox n={child} lvl={level + 1} />
              {child.children && child.children.length > 0 && (
                <div className="border-l border-slate-200 ml-12">
                  {child.children.map(grandchild => (
                    <NodeBox key={grandchild.id} n={grandchild} lvl={level + 2} />
                  ))}
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// Ownership Chart
export function OwnershipChart({ shareholders }) {
  if (!shareholders || shareholders.length === 0) {
    return <div className="text-center py-8 text-slate-500">No shareholder data available</div>;
  }

  const colors = ['#1E3A8A', '#3B82F6', '#60A5FA', '#93C5FD', '#BFDBFE', '#1D4ED8', '#2563EB'];
  
  return (
    <div className="space-y-4">
      {shareholders.map((sh, idx) => (
        <div key={sh.id || idx} className="flex items-center gap-4">
          <div className="w-4 h-4 rounded-sm" style={{ backgroundColor: colors[idx % colors.length] }} />
          <div className="flex-1">
            <div className="flex justify-between items-center mb-1">
              <span className="text-sm text-slate-700">{sh.entity_name}</span>
              <span className="text-sm font-medium text-slate-900">{sh.percentage?.toFixed(2)}%</span>
            </div>
            <div className="w-full bg-slate-100 rounded-full h-2">
              <div className="h-2 rounded-full" style={{ width: `${sh.percentage}%`, backgroundColor: colors[idx % colors.length] }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}
